import React, { createContext, useState, useContext } from 'react';

// Create a context for language
export const LanguageContext = createContext();

// Available languages
export const languages = {
  es: {
    code: 'es',
    name: 'Español',
    flag: '🇲🇽'
  },
  en: {
    code: 'en',
    name: 'English',
    flag: '🇺🇸'
  }
};

// Language provider component
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(languages.es); // Default to Spanish

  const changeLanguage = (langCode) => {
    setLanguage(languages[langCode]);
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => useContext(LanguageContext);

// Translations dictionary
export const translations = {
  // Navigation
  'nav.home': {
    es: 'Inicio',
    en: 'Home'
  },
  'nav.catalog': {
    es: 'Catálogo',
    en: 'Catalog'
  },
  'nav.store': {
    es: 'Tienda',
    en: 'Store'
  },
  'nav.about': {
    es: 'Nosotros',
    en: 'About Us'
  },
  'nav.contact': {
    es: 'Contacto',
    en: 'Contact'
  },
  
  // Home Page
  'home.hero.title': {
    es: 'La nobleza de lo natural',
    en: 'The nobility of nature'
  },
  'home.hero.subtitle': {
    es: 'Muebles y decoración con materiales naturales de la más alta calidad',
    en: 'Furniture and decoration with the highest quality natural materials'
  },
  'home.hero.cta': {
    es: 'Explorar Colección',
    en: 'Explore Collection'
  },
  'home.featured.title': {
    es: 'Productos Destacados',
    en: 'Featured Products'
  },
  'home.featured.subtitle': {
    es: 'Nuestra selección de piezas únicas',
    en: 'Our selection of unique pieces'
  },
  'home.categories.title': {
    es: 'Categorías',
    en: 'Categories'
  },
  'home.about.title': {
    es: 'Descubre Akasha',
    en: 'Discover Akasha'
  },
  'categories.loading': {
    es: 'Cargando categorías...',
    en: 'Loading categories...'
  },
  'categories.notFound': {
    es: 'No se encontraron categorías.',
    en: 'No categories found.'
  },
  'categories.fetchError': {
    es: 'Error al cargar categorías.',
    en: 'Error loading categories.'
  },
  
  // Catalog Page
  'catalog.title': {
    es: 'Nuestro Catálogo',
    en: 'Our Catalog'
  },
  'catalog.filter.title': {
    es: 'Filtros',
    en: 'Filters'
  },
  'catalog.filter.category': {
    es: 'Categoría',
    en: 'Category'
  },
  'catalog.filter.price': {
    es: 'Precio',
    en: 'Price'
  },
  'catalog.filter.sort': {
    es: 'Ordenar por',
    en: 'Sort by'
  },
  'catalog.filter.featured': {
    es: 'Destacados',
    en: 'Featured'
  },
  'catalog.filter.priceLow': {
    es: 'Precio: Menor a Mayor',
    en: 'Price: Low to High'
  },
  'catalog.filter.priceHigh': {
    es: 'Precio: Mayor a Menor',
    en: 'Price: High to Low'
  },
  'catalog.filter.nameAsc': {
    es: 'Nombre: A-Z',
    en: 'Name: A-Z'
  },
  'catalog.filter.nameDesc': {
    es: 'Nombre: Z-A',
    en: 'Name: Z-A'
  },
  'catalog.noProducts': {
    es: 'No se encontraron productos con los filtros seleccionados.',
    en: 'No products found matching the selected filters.'
  },
  'catalog.clearFilters': {
    es: 'Limpiar filtros',
    en: 'Clear filters'
  },
  
  // Store Page
  'store.title': {
    es: 'Tienda Online',
    en: 'Online Store'
  },
  'store.addToCart': {
    es: 'Agregar al carrito',
    en: 'Add to cart'
  },
  'store.outOfStock': {
    es: 'Agotado',
    en: 'Out of stock'
  },
  'store.closed': {
    es: 'La tienda está cerrada por el momento. Pronto estaremos de vuelta.',
    en: 'The store is closed for the time being. We will be back soon.'
  },
  
  // Product Details
  'product.dimensions': {
    es: 'Dimensiones',
    en: 'Dimensions'
  },
  'product.height': {
    es: 'Alto',
    en: 'Height'
  },
  'product.width': {
    es: 'Ancho',
    en: 'Width'
  },
  'product.depth': {
    es: 'Profundidad',
    en: 'Depth'
  },
  'product.material': {
    es: 'Material',
    en: 'Material'
  },
  'product.price': {
    es: 'Precio',
    en: 'Price'
  },
  'product.stock': {
    es: 'Disponibilidad',
    en: 'Availability'
  },
  'product.inStock': {
    es: '{{count}} en stock',
    en: '{{count}} in stock'
  },
  'product.outOfStock': {
    es: 'Agotado',
    en: 'Out of stock'
  },
  'product.onRequest.title': {
    es: 'Bajo pedido',
    en: 'On request'
  },
  'product.onRequest.description': {
    es: 'Este producto puede ser ordenado bajo pedido. Contacta con nosotros para más información.',
    en: 'This product can be ordered on request. Contact us for more information.'
  },
  'product.onRequest.button': {
    es: 'Solicitar por WhatsApp',
    en: 'Request via WhatsApp'
  },
  'product.quantity': {
    es: 'Cantidad',
    en: 'Quantity'
  },
  'product.maxAvailable': {
    es: 'Máximo disponible: {{max}}',
    en: 'Maximum available: {{max}}'
  },
  'product.stockLimit': {
    es: 'Solo hay {{max}} unidades disponibles',
    en: 'Only {{max}} units available'
  },
  'product.description': {
    es: 'Descripción',
    en: 'Description'
  },
  'product.related': {
    es: 'Productos Relacionados',
    en: 'Related Products'
  },
  
  // Shopping Cart
  'cart.title': {
    es: 'Carrito de Compras',
    en: 'Shopping Cart'
  },
  'cart.empty': {
    es: 'Tu carrito está vacío',
    en: 'Your cart is empty'
  },
  'cart.continueShopping': {
    es: 'Continuar comprando',
    en: 'Continue shopping'
  },
  'cart.total': {
    es: 'Total',
    en: 'Total'
  },
  'cart.checkout': {
    es: 'Proceder al pago',
    en: 'Proceed to checkout'
  },
  'cart.items': {
    es: 'artículos',
    en: 'items'
  },
  'cart.button': {
    es: 'Ver carrito',
    en: 'View cart'
  },
  'cart.itemCount': {
    es: '{{count}} artículos',
    en: '{{count}} items'
  },
  'cart.remove': {
    es: 'Eliminar',
    en: 'Remove'
  },
  'cart.quantity': {
    es: 'Cantidad',
    en: 'Quantity'
  },
  
  // Checkout
  'checkout.title': {
    es: 'Proceso de Compra',
    en: 'Checkout'
  },
  'checkout.customerInfo': {
    es: 'Información del Cliente',
    en: 'Customer Information'
  },
  'checkout.name': {
    es: 'Nombre Completo',
    en: 'Full Name'
  },
  'checkout.email': {
    es: 'Email',
    en: 'Email'
  },
  'checkout.phone': {
    es: 'Teléfono',
    en: 'Phone'
  },
  'checkout.shippingInfo': {
    es: 'Información de Envío',
    en: 'Shipping Information'
  },
  'checkout.address': {
    es: 'Dirección',
    en: 'Address'
  },
  'checkout.city': {
    es: 'Ciudad',
    en: 'City'
  },
  'checkout.state': {
    es: 'Estado',
    en: 'State'
  },
  'checkout.zipCode': {
    es: 'Código Postal',
    en: 'Zip Code'
  },
  'checkout.paymentInfo': {
    es: 'Información de Pago',
    en: 'Payment Information'
  },
  'checkout.creditCard': {
    es: 'Tarjeta de Crédito',
    en: 'Credit Card'
  },
  'checkout.bankTransfer': {
    es: 'Transferencia Bancaria',
    en: 'Bank Transfer'
  },
  'checkout.cardNumber': {
    es: 'Número de Tarjeta',
    en: 'Card Number'
  },
  'checkout.cardExpiry': {
    es: 'Fecha de Expiración',
    en: 'Expiration Date'
  },
  'checkout.cardCVC': {
    es: 'CVC',
    en: 'CVC'
  },
  'checkout.orderSummary': {
    es: 'Resumen del Pedido',
    en: 'Order Summary'
  },
  'checkout.completeOrder': {
    es: 'Completar Compra',
    en: 'Complete Order'
  },
  'checkout.previous': {
    es: 'Anterior',
    en: 'Previous'
  },
  'checkout.next': {
    es: 'Siguiente',
    en: 'Next'
  },
  'checkout.thankYou': {
    es: '¡Gracias por tu compra! Tu número de orden es:',
    en: 'Thank you for your purchase! Your order number is:'
  },
  
  // About Page
  'about.title': {
    es: 'Sobre Nosotros',
    en: 'About Us'
  },
  'about.subtitle': {
    es: 'Nuestra Historia',
    en: 'Our Story'
  },
  'about.content': {
    es: 'Akasha Concept Store nace de la pasión por los materiales naturales y el diseño artesanal. Fundada en 2018, nuestra misión es ofrecer piezas únicas que combinen la belleza de lo natural con diseños contemporáneos. Trabajamos directamente con artesanos locales para asegurar la calidad y autenticidad de cada producto.',
    en: 'Akasha Concept Store was born from a passion for natural materials and artisanal design. Founded in 2018, our mission is to offer unique pieces that combine the beauty of nature with contemporary designs. We work directly with local artisans to ensure the quality and authenticity of each product.'
  },
  'about.mission.title': {
    es: 'Nuestra Misión',
    en: 'Our Mission'
  },
  'about.mission.content': {
    es: 'Crear espacios que inspiren a través de la belleza natural y el diseño consciente.',
    en: 'Create spaces that inspire through natural beauty and conscious design.'
  },
  'about.values.title': {
    es: 'Nuestros Valores',
    en: 'Our Values'
  },
  'about.values.sustainability': {
    es: 'Sostenibilidad',
    en: 'Sustainability'
  },
  'about.values.quality': {
    es: 'Calidad',
    en: 'Quality'
  },
  'about.values.authenticity': {
    es: 'Autenticidad',
    en: 'Authenticity'
  },
  // START - New keys for Meet the Designer
  'about.designer.title': {
    es: 'Conoce a la Diseñadora',
    en: 'Meet the Designer'
  },
  'about.designer.name': {
    es: 'Florencia Mariotti',
    en: 'Florencia Mariotti'
  },
  'about.designer.role': {
    es: 'Diseñadora Principal y Fundadora',
    en: 'Lead Designer & Founder'
  },
  'about.designer.bio': {
    es: 'Florencia aporta su pasión por los materiales naturales y el diseño atemporal a cada creación de Akasha, seleccionando piezas que cuentan una historia de arte y alma.',
    en: 'Florencia brings her passion for natural materials and timeless design to every Akasha creation, curating pieces that tell a story of artistry and soul.'
  },
  // END - New keys for Meet the Designer
  
  // Contact Page
  'contact.title': {
    es: 'Contáctanos',
    en: 'Contact Us'
  },
  'contact.subtitle': {
    es: 'Estamos aquí para ayudarte con cualquier consulta o pedido especial',
    en: 'We are here to help you with any questions or special requests'
  },
  'contact.form.name': {
    es: 'Nombre',
    en: 'Name'
  },
  'contact.form.email': {
    es: 'Email',
    en: 'Email'
  },
  'contact.form.message': {
    es: 'Mensaje',
    en: 'Message'
  },
  'contact.form.phoneLabel': {
    es: 'Teléfono',
    en: 'Phone'
  },
  'contact.form.namePlaceholder': {
    es: 'Tu nombre',
    en: 'Your name'
  },
  'contact.form.emailPlaceholder': {
    es: 'tu@email.com',
    en: 'you@example.com'
  },
  'contact.form.phonePlaceholder': {
    es: 'Tu número de teléfono',
    en: 'Your phone number'
  },
  'contact.form.messagePlaceholder': {
    es: '¿En qué podemos ayudarte?',
    en: 'How can we help you?'
  },
  'contact.form.submit': {
    es: 'Enviar Mensaje',
    en: 'Send Message'
  },
  'contact.info.address': {
    es: 'Dirección',
    en: 'Address'
  },
  'contact.info.phone': {
    es: 'Teléfono',
    en: 'Phone'
  },
  'contact.info.email': {
    es: 'Email',
    en: 'Email'
  },
  'contact.info.hours': {
    es: 'Horario',
    en: 'Hours'
  },
  'contact.info.title': {
    es: 'Contáctanos',
    en: 'Contact Us'
  },
  'contact.info.subtitle': {
    es: 'Estamos aquí para ayudarte con cualquier consulta o pedido especial',
    en: 'We are here to help you with any questions or special requests'
  },
  
  // Footer
  'footer.about': {
    es: 'Sobre Akasha',
    en: 'About Akasha'
  },
  'footer.links': {
    es: 'Enlaces Rápidos',
    en: 'Quick Links'
  },
  'footer.social': {
    es: 'Síguenos',
    en: 'Follow Us'
  },
  'footer.newsletter': {
    es: 'Suscríbete a nuestro boletín',
    en: 'Subscribe to our newsletter'
  },
  'footer.subscribe': {
    es: 'Suscribirse',
    en: 'Subscribe'
  },
  'footer.copyright': {
    es: 'Todos los derechos reservados',
    en: 'All rights reserved'
  },
  'footer.privacy': {
    es: 'Aviso de Privacidad',
    en: 'Privacy Policy'
  },
  'footer.terms': {
    es: 'Términos y Condiciones',
    en: 'Terms and Conditions'
  },

  // --- MERGED TRANSLATIONS START --- 
  'filter.title': { es: 'Filtros', en: 'Filters' },
  'filter.categories.title': { es: 'Categorías', en: 'Categories' },
  'filter.categories.all': { es: 'Todas las categorías', en: 'All Categories' },
  'filter.price.title': { es: 'Precio', en: 'Price' },
  'filter.price.upTo': { es: 'Hasta', en: 'Up to' },
  'filter.sortBy.title': { es: 'Ordenar por', en: 'Sort By' },
  'filter.sortBy.featured': { es: 'Destacados', en: 'Featured' },
  'filter.sortBy.priceLow': { es: 'Precio: Menor a Mayor', en: 'Price: Low to High' },
  'filter.sortBy.priceHigh': { es: 'Precio: Mayor a Menor', en: 'Price: High to Low' },
  'filter.sortBy.nameAsc': { es: 'Nombre: A-Z', en: 'Name: A-Z' },
  'filter.sortBy.nameDesc': { es: 'Nombre: Z-A', en: 'Name: Z-A' },

  'product.noImage': { es: 'Sin imagen', en: 'No image' },
  'product.viewDetails': { es: 'Ver detalles', en: 'View Details' },
  'product.addToCart.short': { es: 'Añadir', en: 'Add' },
  'product.addToCart.button': { es: 'Agregar al carrito', en: 'Add to Cart' },
  'product.addToCart.alert': { es: '{{quantity}} x {{name}} agregado(s) al carrito', en: '{{quantity}} x {{name}} added to cart' },
  'product.loading': { es: 'Cargando producto...', en: 'Loading product...' },
  'product.notFound.title': { es: 'Producto no encontrado', en: 'Product Not Found' },
  'product.notFound.message': { es: 'Lo sentimos, el producto que buscas no existe o ha sido removido.', en: 'Sorry, the product you are looking for does not exist or has been removed.' },
  'product.notFound.backButton': { es: 'Volver al catálogo', en: 'Back to Catalog' },
  'product.tabs.description': { es: 'Descripción', en: 'Description' },
  'product.tabs.details': { es: 'Detalles', en: 'Details' },
  'product.tabs.descriptionPlaceholder': { es: 'Este producto forma parte de nuestra exclusiva colección de artículos para el hogar. Cada pieza ha sido cuidadosamente seleccionada para aportar estilo y funcionalidad a tu espacio.', en: 'This product is part of our exclusive collection of home goods. Each piece has been carefully selected to bring style and functionality to your space.' },
  'product.tabs.detailsPlaceholder': { es: 'Detalles adicionales no disponibles.', en: 'Additional details not available.' },
  'product.related.title': { es: 'Productos Relacionados', en: 'Related Products' },

  'store.cartButton': { es: '{{count}} Artículos', en: '{{count}} Items' },
  'store.noProducts': { es: 'No se encontraron productos con los filtros seleccionados.', en: 'No products found matching the selected filters.' },
  'store.clearFilters': { es: 'Limpiar filtros', en: 'Clear filters' },
  'store.checkout.successMessage': { es: '¡Gracias por tu compra! Tu número de orden es: {{orderNumber}}', en: 'Thank you for your purchase! Your order number is: {{orderNumber}}' },

  'breadcrumb.home': { es: 'Inicio', en: 'Home' },
  'breadcrumb.catalog': { es: 'Catálogo', en: 'Catalog' },

  'about.hero.title': { es: 'Sobre Nosotros', en: 'About Us' },
  'about.hero.subtitle': { es: 'Conoce la historia y filosofía detrás de Akasha Concept Store', en: 'Learn about the history and philosophy behind Akasha Concept Store' },
  'about.history.title': { es: 'Nuestra Historia', en: 'Our History' },
  'about.history.p1': { es: 'Akasha Concept Store nació de una profunda apreciación por la belleza inherente de los materiales naturales y la maestría artesanal. Desde nuestros inicios, nos hemos dedicado a curar una colección que no solo embellece los espacios, sino que también cuenta historias de tradición, calidad y un profundo respeto por la naturaleza.', en: 'Akasha Concept Store was born from a deep appreciation for the inherent beauty of natural materials and artisanal craftsmanship. Since our inception, we have dedicated ourselves to curating a collection that not only beautifies spaces but also tells stories of tradition, quality, and a profound respect for nature.' },
  'about.history.p2': { es: 'Creemos que cada pieza de mobiliario y decoración debe ser una inversión en arte y sostenibilidad, fusionando diseño contemporáneo con técnicas ancestrales.', en: 'We believe that each piece of furniture and decor should be an investment in art and sustainability, merging contemporary design with ancestral techniques.' },
  'about.history.imageAlt': { es: 'Interior rústico y elegante de Akasha Concept Store', en: 'Rustic and elegant interior of Akasha Concept Store' },
  'about.philosophy.title': { es: 'Nuestra Filosofía', en: 'Our Philosophy' },
  'about.philosophy.p1': { es: 'En Akasha, la \"nobleza de lo natural\" es más que un lema; es el pilar de nuestra existencia. Nos esforzamos por trabajar en armonía con el medio ambiente, seleccionando materiales sostenibles y colaborando con artesanos locales que comparten nuestra visión.', en: 'At Akasha, the \"nobility of nature\" is more than a motto; it is the pillar of our existence. We strive to work in harmony with the environment, selecting sustainable materials and collaborating with local artisans who share our vision.' },
  'about.philosophy.p2': { es: 'Cada objeto en nuestra tienda es elegido por su capacidad para transformar una casa en un hogar, infundiendo calidez, carácter y una conexión tangible con el mundo natural.', en: 'Each object in our store is chosen for its ability to transform a house into a home, infusing warmth, character, and a tangible connection to the natural world.' },
  'about.philosophy.p3': { es: 'Buscamos inspirar un estilo de vida consciente, donde la belleza y la responsabilidad ambiental coexisten.', en: 'We seek to inspire a conscious lifestyle, where beauty and environmental responsibility coexist.' },
  'about.philosophy.imageAlt': { es: 'Detalle de texturas naturales en productos de Akasha', en: 'Detail of natural textures in Akasha products' },
  'about.team.title': { es: 'Nuestro Equipo', en: 'Our Team' },
  'about.team.member1.name': { es: 'María González', en: 'Maria Gonzalez' },
  'about.team.member1.role': { es: 'Fundadora & Directora Creativa', en: 'Founder & Creative Director' },
  'about.team.member2.name': { es: 'Carlos Mendoza', en: 'Carlos Mendoza' },
  'about.team.member2.role': { es: 'Director de Operaciones', en: 'Operations Manager' },
  'about.team.member3.name': { es: 'Laura Jiménez', en: 'Laura Jimenez' },
  'about.team.member3.role': { es: 'Coordinadora de Artesanos', en: 'Artisan Coordinator' },

  'contact.hero.title': { es: 'Contáctanos', en: 'Contact Us' },
  'contact.hero.subtitle': { es: 'Estamos aquí para ayudarte con cualquier consulta o pedido especial', en: 'We are here to help you with any questions or special requests' },
  'contact.info.address.title': { es: 'Dirección', en: 'Address' },
  'contact.info.address.line1': { es: 'Av. Miguel Ángel de Quevedo 247,', en: 'Av. Miguel Ángel de Quevedo 247,' },
  'contact.info.address.line2': { es: 'Romero de Terreros, Coyoacán,', en: 'Romero de Terreros, Coyoacán,' },
  'contact.info.address.line3': { es: '04310 Ciudad de México, CDMX', en: '04310 Mexico City, CDMX' },
  'contact.info.phone.title': { es: 'Teléfono', en: 'Phone' },
  'contact.info.phone.number': { es: '55 1160 5388', en: '+52 55 1160 5388' },
  'contact.info.email.title': { es: 'Email', en: 'Email' },
  'contact.info.email.address': { es: 'akashaconceptstore@gmail.com', en: 'akashaconceptstore@gmail.com' },
  'contact.info.hours.title': { es: 'Horario', en: 'Hours' },
  'contact.info.hours.time': { es: 'Todos los días: 11:00 am - 9:00 pm', en: 'Every day: 11:00 am - 9:00 pm' },
  'contact.info.social.title': { es: 'Síguenos', en: 'Follow Us' },
  'contact.form.title': { es: 'Envíanos un Mensaje', en: 'Send Us a Message' },
  'contact.map.heading': { es: 'Ubicación', en: 'Location' },
  'contact.map.title': { es: 'Mapa de Akasha Concept Store', en: 'Akasha Concept Store Map' },
  // --- MERGED TRANSLATIONS END --- 

  'store.product.buyButton': {
    es: 'Comprar',
    en: 'Buy Now'
  },
  'pagination.previous': {
    es: 'Anterior',
    en: 'Previous'
  },
  'pagination.next': {
    es: 'Siguiente',
    en: 'Next'
  },
  'pagination.page': {
    es: 'Página',
    en: 'Page'
  },
  'pagination.of': {
    es: 'de',
    en: 'of'
  }
};

// Translation function (ensure interpolation works if needed)
export const t = (key, lang, options = {}) => {
  let translation = key; // Default to key if not found
  if (translations[key] && translations[key][lang]) {
    translation = translations[key][lang];
  }

  // Basic interpolation (replace {{key}} with value)
  Object.keys(options).forEach((optionKey) => {
    const regex = new RegExp(`{{${optionKey}}}`, 'g'); 
    translation = translation.replace(regex, options[optionKey]);
  });

  if (translation === key && !key.startsWith('{{')) {
    console.warn(`Translation key not found or missing for lang '${lang}': ${key}`);
  }

  return translation;
};
